/**
 * Fetches a file as a Blob and generates an object URL along with its content type.
 *
 * - If the file is hosted on `.s3.ap-south-1.amazonaws.com`, request will skip custom headers.
 * - Returns an object containing a blob URL (for rendering/preview) and the file's content type.
 * - On failure, falls back to returning the original `fileUrl` with `contentType: null`.
 *
 * @async
 * @function getBlobUrlAndContentType
 * @param {Object} params - The input parameters.
 * @param {string} params.fileUrl - The URL of the file to fetch.
 * @param {Object<string, string>} [params.headers] - Optional headers for external requests.
 * @returns {Promise<{ blobUrl: string, contentType: string|null }>} 
 *   An object containing:
 *   - `blobUrl`: A blob-based object URL or the original fileUrl if fetch fails.
 *   - `contentType`: MIME type string (e.g., "image/png") or `null` if unavailable.
 */
import { store } from '../store/index';


export const getBlobUrlAndContentType = async ({ fileUrl, headers = {} }) => {
    try {
        const isExternal = new URL(fileUrl).hostname.endsWith('.amazonaws.com');
        const response = await fetch(fileUrl, {
            method: 'GET',
            ...(!isExternal ? { headers } : {}),
        });

        if (!response.ok) {
            throw new Error(`Fetch failed with status ${response.status}`);
        }

        const contentType = response.headers.get('content-type') || 'application/octet-stream';
        const blob = await response.blob();

        return {
            blobUrl: URL.createObjectURL(blob),
            contentType,
        };
    } catch (err) {
        console.error('Failed to fetch blob:', { fileUrl, err });
        return { blobUrl: fileUrl, contentType: null };
    }
}


export const getBlobUrl = async (fileUrl) => {
    const state = store.getState();
    if (state.user.authToken) {
        return await getBlobUrlAndContentType({
            fileUrl: fileUrl,
            headers: { Authorization: state.user.authToken }
        }).then(res => res.blobUrl);
    }
}